/**
 * iter921L - Describe an approved transaction for the success toast.
 *
 * Priority order (most specific wins):
 *   1. Document type (Invoice / Bill / Fixed Asset / Loan / Prepayment /
 *      Investment / Other Receipt / Other Payment) - the actual document
 *      we just created.
 *   2. GL account name (e.g. "Sales Revenue", "Bank Charges", "Owner's
 *      Equity") - what the journal hit.
 *   3. Sub-category, pretty-printed (e.g. "Capital Contribution").
 *
 * The two layers together mean: "Recorded as Invoice - Sales Revenue" or
 * "Recorded as Other Receipt - Owner's Equity (Capital Contribution)".
 *
 * Critical: this function reads from the *post-mutation* `item` so that
 * if the user inline-changed the GL category before approving (iter921j/L),
 * the toast reflects the NEW classification, not the AI's stale guess.
 */
const DOC_TYPE_LABELS = {
  invoice: 'Invoice',
  bill: 'Bill',
  fixed_asset: 'Fixed Asset',
  prepayment: 'Prepayment',
  loan: 'Loan',
  loan_receivable: 'Loan Receivable',
  investment: 'Investment',
  other_receipt: 'Other Receipt',
  other_payment: 'Other Payment',
  bank_charge: 'Bank Charge',
  transfer: 'Bank Transfer',
};

const SUB_CATEGORY_LABELS = {
  capital_contribution: 'Capital Contribution',
  owner_drawing: 'Owner Drawing',
  owner_drawings: 'Owner Drawing',
  loan_received: 'Loan Received',
  loan_repayment: 'Loan Repayment',
  loan_given: 'Loan Given',
  investment_made: 'Investment Made',
  fixed_asset_purchase: 'Fixed Asset Purchase',
  supplier_prepayment: 'Supplier Prepayment',
  bank_charges: 'Bank Charges',
  bank_charge: 'Bank Charge',
  tax_payment: 'Tax Payment',
  salary_payment: 'Salary Payment',
  expense: 'Expense',
  revenue: 'Revenue',
  other_inflow: 'Other Inflow',
  other_outflow: 'Other Outflow',
};

function prettify(slug) {
  if (!slug) return null;
  if (SUB_CATEGORY_LABELS[slug]) return SUB_CATEGORY_LABELS[slug];
  return String(slug)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function describeApprovedTx(item) {
  if (!item) return 'transaction';
  const direction = (item.direction || '').toLowerCase();
  const docType = item.ai_document_type || item.document_type;
  const subCat = item.ai_sub_category || item.classification;
  const isInflow = direction === 'inflow';
  // For outflows the GL the journal DEBITS is the meaningful one
  // (expense / asset / equity-draw); for inflows the GL the journal
  // CREDITS is the meaningful one (revenue / liability-loan / equity-cap).
  const glName = isInflow
    ? (item.ai_gl_credit_name || item.ai_gl_debit_name)
    : (item.ai_gl_debit_name || item.ai_gl_credit_name);

  const docLabel = DOC_TYPE_LABELS[docType] || (docType ? prettify(docType) : null);
  const subLabel = prettify(subCat);

  // Build: "Invoice - Sales Revenue (Capital Contribution)" - but drop the
  // sub-category parens when it would just echo the GL name or the doc
  // label (e.g. "Other Payment - Bank Charges (Bank Charges)" → drop).
  const subEchoesGl = subLabel && glName &&
    subLabel.toLowerCase() === glName.toLowerCase();
  const subEchoesDoc = subLabel && docLabel &&
    subLabel.toLowerCase() === docLabel.toLowerCase();
  if (docLabel && glName && subLabel && !subEchoesGl && !subEchoesDoc) {
    return `${docLabel} · ${glName} (${subLabel})`;
  }
  if (docLabel && glName) return `${docLabel} · ${glName}`;
  if (docLabel && subLabel) return `${docLabel} (${subLabel})`;
  if (docLabel) return docLabel;
  if (glName) return glName;
  if (subLabel) return subLabel;
  return 'transaction';
}

export default describeApprovedTx;
